import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PageHeader } from '../../../components/ui/PageHeader'
import { Spinner } from '../../../components/ui/Spinner'
import { Alert } from '../../../components/ui/Alert'
import { selectClass } from '../../../components/ui/FormField'
import { useCampeonatos } from '../../campeonatos/hooks/useCampeonatos'
import { useModalidades } from '../../modalidades/hooks/useModalidades'
import { useEtapas } from '../../etapas/hooks/useEtapas'
import { useRankingEtapa, useRankingGeral, useRecalcularRanking } from '../hooks/useRankings'
import { rankingsService } from '../services/rankings.service'
import type { RankingEtapaItem, RankingGeralItem, RankingEtapaResponse, RankingGeralResponse } from '../types'

// ── Helpers ───────────────────────────────────────────────────────────────────

function medalha(pos: number): string {
  if (pos === 1) return '🥇'
  if (pos === 2) return '🥈'
  if (pos === 3) return '🥉'
  return String(pos)
}

// ── Tabela de Ranking da Etapa ────────────────────────────────────────────────

function TabelaRankingEtapa({
  rankingData,
  modalidadeNome,
  etapaNome,
  isPrecisao,
  isSilhueta,
  isIpsc,
}: {
  rankingData: RankingEtapaResponse
  modalidadeNome: string
  etapaNome: string
  isPrecisao: boolean
  isSilhueta: boolean
  isIpsc: boolean
}) {
  return (
    <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-hidden">
      <div className="bg-compumaq-preto border-b border-compumaq-dourado/25 px-4 py-3 flex items-center justify-between">
        <div>
          <span className="font-semibold text-compumaq-texto">{modalidadeNome}</span>
          <span className="ml-2 text-xs text-compumaq-textoSuave">
            {etapaNome ? `· ${etapaNome}` : ''} · {rankingData.data.length} classificados
          </span>
        </div>
        {rankingData.meta.regras_desempate.length > 0 && (
          <div className="text-xs text-compumaq-textoSuave hidden sm:block">
            Desempate: {rankingData.meta.regras_desempate.map(r => r.campo).join(' → ')}
          </div>
        )}
      </div>

      {rankingData.data.length === 0 ? (
        <div className="text-center py-12 text-compumaq-textoSuave">
          <p>Nenhum resultado lancado nesta etapa ainda</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave w-16">Pos.</th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Atirador</th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Clube</th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Cat.</th>
                {isPrecisao && (
                  <>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">Total Pts</th>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">X</th>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">10</th>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">9</th>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">8</th>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">7</th>
                  </>
                )}
                {isSilhueta && (
                  <>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">Total</th>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">Carneiro</th>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">Peru</th>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">Porco</th>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">Galinha</th>
                  </>
                )}
                {isIpsc && (
                  <>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">Hit Factor</th>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">Pontos</th>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">Tempo</th>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">A</th>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">C</th>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">D</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-compumaq-dourado/25">
              {rankingData.data.map((item: RankingEtapaItem) => (
                <tr key={item.idinscricao} className={item.posicao <= 3 ? 'bg-yellow-50/40' : 'hover:bg-compumaq-preto'}>
                  <td className="px-4 py-3 font-bold text-lg text-compumaq-texto">{medalha(item.posicao)}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-compumaq-texto">{item.atirador_nome}</p>
                    <p className="text-xs text-compumaq-textoSuave font-mono">{item.atirador_cpf}</p>
                  </td>
                  <td className="px-4 py-3 text-compumaq-textoSuave">{item.clube_nome}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.categoria === 'dama' ? 'bg-pink-100 text-pink-700' : 'bg-compumaq-dourado/15 text-compumaq-doradoClaro'}`}>
                      {item.categoria}
                    </span>
                  </td>
                  {isPrecisao && (
                    <>
                      <td className="px-3 py-3 text-right font-bold text-compumaq-texto">{item.total_pontos}</td>
                      <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.qtd_x}</td>
                      <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.qtd_10}</td>
                      <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.qtd_9}</td>
                      <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.qtd_8}</td>
                      <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.qtd_7}</td>
                    </>
                  )}
                  {isSilhueta && (
                    <>
                      <td className="px-3 py-3 text-right font-bold text-compumaq-texto">{item.total}</td>
                      <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.carneiro}</td>
                      <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.peru}</td>
                      <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.porco}</td>
                      <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.galinha}</td>
                    </>
                  )}
                  {isIpsc && (
                    <>
                      <td className="px-3 py-3 text-right font-bold text-compumaq-texto">{Number(item.hit_factor ?? 0).toFixed(4)}</td>
                      <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.total_pontos}</td>
                      <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.tempo_segundos ?? 0}s</td>
                      <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.qtd_a}</td>
                      <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.qtd_c}</td>
                      <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.qtd_d}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Tabela de Ranking Geral ───────────────────────────────────────────────────

function TabelaRankingGeral({
  rankingData,
  modalidadeNome,
}: {
  rankingData: RankingGeralResponse
  modalidadeNome: string
}) {
  const isPrecisao = rankingData.meta.modalidade_grupo === 'Precisao'
  const isSilhueta = rankingData.meta.modalidade_grupo === 'Silhueta'
  const isIpsc = rankingData.meta.modalidade_grupo === 'IPSC'

  return (
    <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-hidden">
      <div className="bg-compumaq-preto border-b border-compumaq-dourado/25 px-4 py-3 flex items-center justify-between">
        <div>
          <span className="font-semibold text-compumaq-texto">{modalidadeNome}</span>
          <span className="ml-2 text-xs text-compumaq-textoSuave">
            · {rankingData.data.length} atiradores classificados
          </span>
        </div>
        {rankingData.meta.regras_desempate.length > 0 && (
          <div className="text-xs text-compumaq-textoSuave hidden sm:block">
            Desempate: {rankingData.meta.regras_desempate.map(r => r.campo).join(' → ')}
          </div>
        )}
      </div>

      {rankingData.meta.flag_descarta_etapa && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-sm text-amber-700">
          Descarte ativo: {rankingData.meta.numero_descarte_etapa} pior(es) etapa(s) descartada(s) por atirador
        </div>
      )}

      {rankingData.data.length === 0 ? (
        <div className="text-center py-12 text-compumaq-textoSuave">Nenhum resultado registrado ainda</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave w-16">Pos.</th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Atirador</th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Clube</th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Cat.</th>
                <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">Etapas</th>
                {rankingData.meta.flag_descarta_etapa && (
                  <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">Desc.</th>
                )}
                <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave text-compumaq-doradoClaro">
                  {isPrecisao ? 'Total Pts' : isIpsc ? 'Total %' : 'Total'}
                </th>
                {isPrecisao && (
                  <>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">X</th>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">10</th>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">9</th>
                  </>
                )}
                {isSilhueta && (
                  <>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">Carneiro</th>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">Peru</th>
                    <th className="text-right px-3 py-3 font-medium text-compumaq-textoSuave">Porco</th>
                  </>
                )}
                {rankingData.meta.etapas.map(e => (
                  <th key={e.idetapa} className="text-right px-2 py-3 font-medium text-compumaq-textoSuave text-xs">
                    {e.titulo_reduzido}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-compumaq-dourado/25">
              {rankingData.data.map((item: RankingGeralItem) => (
                <tr key={item.idatirador} className={item.posicao <= 3 ? 'bg-yellow-50/40' : 'hover:bg-compumaq-preto'}>
                  <td className="px-4 py-3 font-bold text-lg text-compumaq-texto">{medalha(item.posicao)}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-compumaq-texto">{item.atirador_nome}</p>
                    <p className="text-xs text-compumaq-textoSuave font-mono">{item.atirador_cpf}</p>
                  </td>
                  <td className="px-4 py-3 text-compumaq-textoSuave">{item.clube_nome}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.categoria === 'dama' ? 'bg-pink-100 text-pink-700' : 'bg-compumaq-dourado/15 text-compumaq-doradoClaro'}`}>
                      {item.categoria}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.etapas_contadas}</td>
                  {rankingData.meta.flag_descarta_etapa && (
                    <td className="px-3 py-3 text-right text-amber-600 text-xs">{item.etapas_descartadas || '—'}</td>
                  )}
                  <td className="px-3 py-3 text-right font-bold text-compumaq-doradoClaro text-base">
                    {isIpsc ? `${Number(item.total_final).toFixed(2)}%` : item.total_final}
                  </td>
                  {isPrecisao && (
                    <>
                      <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.soma_qtd_x}</td>
                      <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.soma_qtd_10}</td>
                      <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.soma_qtd_9}</td>
                    </>
                  )}
                  {isSilhueta && (
                    <>
                      <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.soma_carneiro}</td>
                      <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.soma_peru}</td>
                      <td className="px-3 py-3 text-right text-compumaq-textoSuave">{item.soma_porco}</td>
                    </>
                  )}
                  {rankingData.meta.etapas.map(etapa => {
                    const etapaItem = item.etapas.find(e => e.idetapa === etapa.idetapa)
                    return (
                      <td key={etapa.idetapa} className={`px-2 py-3 text-right text-xs ${etapaItem?.descartada ? 'text-red-400 line-through' : 'text-compumaq-textoSuave'}`}>
                        {etapaItem ? (isIpsc ? `${etapaItem.total.toFixed(1)}%` : etapaItem.total) : <span className="text-compumaq-textoSuave">—</span>}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Componente Principal ──────────────────────────────────────────────────────

export function RankingsPage() {
  const [activeTab, setActiveTab] = useState<'etapa' | 'geral'>('etapa')

  const [selCampeonato, setSelCampeonato] = useState<number | ''>('')
  const [selModalidade, setSelModalidade] = useState<number | 'all' | ''>('')
  const [selEtapa, setSelEtapa] = useState<number | 'todas' | ''>('')
  const [modalidadeGrupo, setModalidadeGrupo] = useState<string>('')

  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const { data: campeonatosData } = useCampeonatos({ limit: 200 })
  const { data: modalidadesData } = useModalidades(
    selCampeonato ? { idcampeonato: selCampeonato as number, limit: 100 } : {},
  )
  const modalidades = selCampeonato ? (modalidadesData?.data ?? []) : []

  const { data: etapasData } = useEtapas(
    selCampeonato && selModalidade && selModalidade !== 'all'
      ? { idcampeonato: selCampeonato as number, idmodalidade: selModalidade as number, limit: 50 }
      : {},
  )
  const etapas = selCampeonato && selModalidade && selModalidade !== 'all' ? (etapasData?.data ?? []) : []

  // Etapas do campeonato sem filtro de modalidade (usado quando "Todas as Modalidades"
  // esta selecionada). A tabela `etapa` tem uma linha por modalidade para a mesma rodada,
  // entao esta lista tem repeticao por design - a deduplicacao para exibicao no filtro
  // acontece em `etapasCampeonatoUnicas`, mais abaixo.
  const { data: etapasCampeonatoData } = useEtapas(
    selCampeonato && selModalidade === 'all'
      ? { idcampeonato: selCampeonato as number, limit: 50 }
      : {},
  )
  const etapasCampeonato = selCampeonato && selModalidade === 'all' ? (etapasCampeonatoData?.data ?? []) : []

  // Etapas unicas por numero_etapa, para o filtro nao repetir uma opcao por modalidade.
  const etapasCampeonatoUnicas = (() => {
    const vistas = new Map<number, typeof etapasCampeonato[0]>()
    for (const e of etapasCampeonato) {
      if (!vistas.has(e.numero_etapa)) vistas.set(e.numero_etapa, e)
    }
    return Array.from(vistas.values()).sort((a, b) => a.numero_etapa - b.numero_etapa)
  })()

  // "Todas as etapas" selecionado na aba Ranking da Etapa: reaproveita o Ranking Geral
  const mostrarGeralNaAbaEtapa = activeTab === 'etapa' && selEtapa === 'todas'

  // Ranking da etapa para modalidade especifica
  const { data: rankingEtapaData, isLoading: loadingEtapa } = useRankingEtapa({
    idcampeonato: selCampeonato !== '' ? selCampeonato as number : null,
    idmodalidade: selModalidade !== '' && selModalidade !== 'all' ? selModalidade as number : null,
    idetapa: activeTab === 'etapa' && typeof selEtapa === 'number' ? selEtapa : null,
  })

  // Ranking geral para modalidade especifica (aba "Ranking Geral" OU "Todas as etapas" na aba Ranking da Etapa)
  const { data: rankingGeralData, isLoading: loadingGeral } = useRankingGeral({
    idcampeonato: selCampeonato !== '' ? selCampeonato as number : null,
    idmodalidade: (activeTab === 'geral' || mostrarGeralNaAbaEtapa) && selModalidade !== '' && selModalidade !== 'all' ? selModalidade as number : null,
  })

  // Ranking da etapa para TODAS as modalidades (quando selModalidade === 'all' e uma etapa
  // especifica, nao "todas", foi escolhida). O filtro guarda o NUMERO da etapa (numero_etapa),
  // nao um idetapa - cada modalidade tem sua propria linha na tabela `etapa` para a mesma
  // rodada, entao aqui resolvemos o idetapa de cada modalidade para aquele numero_etapa.
  const { data: rankingEtapaTodasData, isLoading: loadingEtapaAll } = useQuery({
    queryKey: ['ranking-etapa-todas', selCampeonato, selEtapa],
    queryFn: async () => {
      if (!selCampeonato || selEtapa === '' || selEtapa === 'todas' || selModalidade !== 'all') return null
      const numeroEtapa = selEtapa
      const results: Array<{ modalidade: typeof modalidades[0]; ranking: RankingEtapaResponse }> = []
      for (const mod of modalidades) {
        const etapaDaModalidade = etapasCampeonato.find(
          e => e.idmodalidade === mod.idmodalidade && e.numero_etapa === numeroEtapa,
        )
        if (!etapaDaModalidade) continue
        try {
          const data = await rankingsService.getEtapa({
            idcampeonato: selCampeonato as number,
            idmodalidade: mod.idmodalidade,
            idetapa: etapaDaModalidade.idetapa,
          })
          if (data.data.length > 0) {
            results.push({ modalidade: mod, ranking: data })
          }
        } catch {
          // pular modalidades sem resultado
        }
      }
      return results
    },
    enabled: activeTab === 'etapa' && selModalidade === 'all' && !!selCampeonato && typeof selEtapa === 'number' && modalidades.length > 0,
    staleTime: 0,
  })

  // Ranking geral para TODAS as modalidades
  const { data: rankingGeralTodasData, isLoading: loadingGeralAll } = useQuery({
    queryKey: ['ranking-geral-todas', selCampeonato, modalidades.map(m => m.idmodalidade)],
    queryFn: async () => {
      if (!selCampeonato || selModalidade !== 'all') return null
      const results: Array<{ modalidade: typeof modalidades[0]; ranking: RankingGeralResponse }> = []
      for (const mod of modalidades) {
        try {
          const data = await rankingsService.getGeral({
            idcampeonato: selCampeonato as number,
            idmodalidade: mod.idmodalidade,
          })
          if (data.data.length > 0) {
            results.push({ modalidade: mod, ranking: data })
          }
        } catch {
          // pular modalidades sem resultado
        }
      }
      return results
    },
    enabled: (activeTab === 'geral' || mostrarGeralNaAbaEtapa) && selModalidade === 'all' && !!selCampeonato && modalidades.length > 0,
    staleTime: 0,
  })

  const recalcular = useRecalcularRanking()

  function handleModalidadeChange(val: string) {
    if (val === 'all') {
      setSelModalidade('all')
      setSelEtapa('')
      setModalidadeGrupo('')
    } else {
      const id = val ? parseInt(val) : ''
      setSelModalidade(id)
      setSelEtapa('')
      const mod = modalidades.find(m => m.idmodalidade === id)
      setModalidadeGrupo(mod?.grupo ?? '')
    }
  }

  async function handleRecalcular() {
    if (selCampeonato === '') return
    try {
      await recalcular.mutateAsync({
        idcampeonato: selCampeonato as number,
        ...(selModalidade !== '' && selModalidade !== 'all' ? { idmodalidade: selModalidade as number } : {}),
      })
      setAlert({ type: 'success', message: 'Ranking recalculado com sucesso' })
    } catch {
      setAlert({ type: 'error', message: 'Erro ao recalcular ranking' })
    }
  }

  const isPrecisao = modalidadeGrupo === 'Precisao'
  const isSilhueta = modalidadeGrupo === 'Silhueta'
  const isIpsc = modalidadeGrupo === 'IPSC'

  const isLoadingAll = (activeTab === 'etapa' && selModalidade === 'all' && !mostrarGeralNaAbaEtapa && loadingEtapaAll) ||
    ((activeTab === 'geral' || mostrarGeralNaAbaEtapa) && selModalidade === 'all' && loadingGeralAll)

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <PageHeader
        title="Rankings"
        description="Classificacao por etapa e ranking geral do campeonato"
        action={
          selCampeonato !== '' && (
            <button
              onClick={handleRecalcular}
              disabled={recalcular.isPending}
              className="px-4 py-2 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto disabled:opacity-50"
            >
              {recalcular.isPending ? 'Recalculando...' : 'Recalcular'}
            </button>
          )
        }
      />

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      {/* Tabs */}
      <div className="flex gap-1 bg-compumaq-pretoSuave rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab('etapa')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'etapa' ? 'bg-compumaq-pretoSuave shadow text-compumaq-texto' : 'text-compumaq-textoSuave hover:text-compumaq-texto'}`}
        >
          Ranking da Etapa
        </button>
        <button
          onClick={() => setActiveTab('geral')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'geral' ? 'bg-compumaq-pretoSuave shadow text-compumaq-texto' : 'text-compumaq-textoSuave hover:text-compumaq-texto'}`}
        >
          Ranking Geral
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm">
        <div className={`grid gap-3 ${activeTab === 'etapa' ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'}`}>
          <div>
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Campeonato</label>
            <select
              className={selectClass}
              value={selCampeonato}
              onChange={e => {
                setSelCampeonato(e.target.value ? parseInt(e.target.value) : '')
                setSelModalidade('')
                setSelEtapa('')
                setModalidadeGrupo('')
              }}
            >
              <option value="">Selecione...</option>
              {campeonatosData?.data?.map(c => (
                <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao} — {c.temporada}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Modalidade</label>
            <select
              className={selectClass}
              value={selModalidade}
              onChange={e => handleModalidadeChange(e.target.value)}
              disabled={!selCampeonato}
            >
              <option value="">{selCampeonato ? 'Selecione...' : '— selecione o campeonato —'}</option>
              {selCampeonato && <option value="all">Todas as Modalidades</option>}
              {modalidades.map(m => (
                <option key={m.idmodalidade} value={m.idmodalidade}>
                  {m.nome}{m.divisao ? ` — ${m.divisao}` : ''} ({m.grupo})
                </option>
              ))}
            </select>
          </div>
          {activeTab === 'etapa' && (
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Etapa</label>
              {selModalidade === 'all' ? (
                <select
                  className={selectClass}
                  value={selEtapa}
                  onChange={e => setSelEtapa(e.target.value === '' ? '' : e.target.value === 'todas' ? 'todas' : parseInt(e.target.value))}
                  disabled={!selCampeonato}
                >
                  <option value="">{selCampeonato ? 'Selecione...' : '— selecione o campeonato —'}</option>
                  {selCampeonato && <option value="todas">Todas as etapas</option>}
                  {etapasCampeonatoUnicas.map(e => (
                    <option key={e.numero_etapa} value={e.numero_etapa}>{e.titulo_reduzido || `Etapa ${e.numero_etapa}`}</option>
                  ))}
                </select>
              ) : (
                <select
                  className={selectClass}
                  value={selEtapa}
                  onChange={e => setSelEtapa(e.target.value === '' ? '' : e.target.value === 'todas' ? 'todas' : parseInt(e.target.value))}
                  disabled={!selModalidade}
                >
                  <option value="">{selModalidade ? 'Selecione...' : '— selecione a modalidade —'}</option>
                  {selModalidade !== '' && <option value="todas">Todas as etapas</option>}
                  {etapas.map(e => (
                    <option key={e.idetapa} value={e.idetapa}>{e.titulo_reduzido || `Etapa ${e.numero_etapa}`}</option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Tab: Ranking da Etapa ─────────────────────────────────────────── */}
      {activeTab === 'etapa' && (
        <>
          {(loadingEtapa || isLoadingAll) && <div className="flex justify-center py-16"><Spinner size="lg" /></div>}

          {/* Placeholder quando nenhuma modalidade selecionada */}
          {!loadingEtapa && !isLoadingAll && selModalidade === '' && (
            <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-12 text-center text-compumaq-textoSuave">
              <p className="text-3xl mb-2">🏆</p>
              <p>Selecione campeonato, modalidade e etapa para ver o ranking</p>
            </div>
          )}

          {/* Todas as Modalidades — "Todas as etapas": reaproveita o Ranking Geral (colunas por etapa + Total, ordenado pelo maior total) */}
          {!isLoadingAll && selModalidade === 'all' && selEtapa === 'todas' && (
            <>
              {(!rankingGeralTodasData || rankingGeralTodasData.length === 0) ? (
                <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-12 text-center text-compumaq-textoSuave">
                  <p className="text-3xl mb-2">🏆</p>
                  <p>Nenhum resultado registrado ainda</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {rankingGeralTodasData.map(({ modalidade: mod, ranking }) => (
                    <TabelaRankingGeral
                      key={mod.idmodalidade}
                      rankingData={ranking}
                      modalidadeNome={`${mod.nome}${mod.divisao ? ` — ${mod.divisao}` : ''} (${mod.grupo})`}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Todas as Modalidades — uma etapa especifica */}
          {!isLoadingAll && selModalidade === 'all' && typeof selEtapa === 'number' && (
            <>
              {(!rankingEtapaTodasData || rankingEtapaTodasData.length === 0) ? (
                <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-12 text-center text-compumaq-textoSuave">
                  <p className="text-3xl mb-2">🏆</p>
                  <p>Nenhum resultado lancado nesta etapa ainda</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {rankingEtapaTodasData.map(({ modalidade: mod, ranking }) => (
                    <TabelaRankingEtapa
                      key={mod.idmodalidade}
                      rankingData={ranking}
                      modalidadeNome={`${mod.nome}${mod.divisao ? ` — ${mod.divisao}` : ''} (${mod.grupo})`}
                      etapaNome={etapasCampeonatoUnicas.find(e => e.numero_etapa === selEtapa)?.titulo_reduzido ?? ''}
                      isPrecisao={mod.grupo === 'Precisao'}
                      isSilhueta={mod.grupo === 'Silhueta'}
                      isIpsc={mod.grupo === 'IPSC'}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Placeholder Todas selecionadas mas sem etapa */}
          {!isLoadingAll && selModalidade === 'all' && selEtapa === '' && (
            <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-12 text-center text-compumaq-textoSuave">
              <p className="text-3xl mb-2">🏆</p>
              <p>Selecione uma etapa para ver o ranking de todas as modalidades</p>
            </div>
          )}

          {/* Modalidade especifica */}
          {!loadingEtapa && selModalidade !== '' && selModalidade !== 'all' && selEtapa === '' && (
            <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-12 text-center text-compumaq-textoSuave">
              <p className="text-3xl mb-2">🏆</p>
              <p>Selecione uma etapa para ver o ranking</p>
            </div>
          )}

          {/* Modalidade especifica — "Todas as etapas": reaproveita o Ranking Geral (colunas por etapa + Total, ordenado pelo maior total) */}
          {!loadingGeral && selModalidade !== '' && selModalidade !== 'all' && selEtapa === 'todas' && rankingGeralData && (
            <>
              {rankingGeralData.meta.flag_descarta_etapa && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-700">
                  Descarte ativo: {rankingGeralData.meta.numero_descarte_etapa} pior(es) etapa(s) descartada(s) por atirador
                </div>
              )}
              <TabelaRankingGeral
                rankingData={rankingGeralData}
                modalidadeNome={modalidades.find(m => m.idmodalidade === selModalidade)?.nome ?? '—'}
              />
            </>
          )}

          {!loadingEtapa && selModalidade !== '' && selModalidade !== 'all' && typeof selEtapa === 'number' && rankingEtapaData && (
            <TabelaRankingEtapa
              rankingData={rankingEtapaData}
              modalidadeNome={modalidades.find(m => m.idmodalidade === selModalidade)?.nome ?? '—'}
              etapaNome={etapas.find(e => e.idetapa === selEtapa)?.titulo_reduzido ?? ''}
              isPrecisao={isPrecisao}
              isSilhueta={isSilhueta}
              isIpsc={isIpsc}
            />
          )}
        </>
      )}

      {/* ── Tab: Ranking Geral ────────────────────────────────────────────── */}
      {activeTab === 'geral' && (
        <>
          {(loadingGeral || isLoadingAll) && <div className="flex justify-center py-16"><Spinner size="lg" /></div>}

          {/* Placeholder quando nenhuma modalidade selecionada */}
          {!loadingGeral && !isLoadingAll && selModalidade === '' && (
            <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-12 text-center text-compumaq-textoSuave">
              <p className="text-3xl mb-2">🏆</p>
              <p>Selecione campeonato e modalidade para ver o ranking geral</p>
            </div>
          )}

          {/* Todas as Modalidades */}
          {!isLoadingAll && selModalidade === 'all' && (
            <>
              {(!rankingGeralTodasData || rankingGeralTodasData.length === 0) ? (
                <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-12 text-center text-compumaq-textoSuave">
                  <p className="text-3xl mb-2">🏆</p>
                  <p>Nenhum resultado registrado ainda</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {rankingGeralTodasData.map(({ modalidade: mod, ranking }) => (
                    <TabelaRankingGeral
                      key={mod.idmodalidade}
                      rankingData={ranking}
                      modalidadeNome={`${mod.nome}${mod.divisao ? ` — ${mod.divisao}` : ''} (${mod.grupo})`}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Modalidade especifica */}
          {!loadingGeral && selModalidade !== '' && selModalidade !== 'all' && rankingGeralData && (
            <>
              {rankingGeralData.meta.flag_descarta_etapa && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-700">
                  Descarte ativo: {rankingGeralData.meta.numero_descarte_etapa} pior(es) etapa(s) descartada(s) por atirador
                </div>
              )}
              <TabelaRankingGeral
                rankingData={rankingGeralData}
                modalidadeNome={modalidades.find(m => m.idmodalidade === selModalidade)?.nome ?? '—'}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}
